/* Stickiply Learn — page behaviours (ARCHITECTURE.md §5, §6, §11).
 *
 * Vanilla, deferred, no dependencies. One file for every Learn template.
 * Each behaviour binds by data attribute and no-ops if its markup is absent,
 * so the same script is safe on the hub, an article, a topic and a path.
 *
 * Phase 1 boundary — the interaction backend (§3c Postgres) does not exist
 * yet, so anything per-customer is CLIENT-ONLY here, and honest about it:
 *   likes/saves    → localStorage under learn:*  (merged into the account later, §3c)
 *   helpful votes  → POST to the proxy shape /apps/learn/api/helpful, fire-and-forget,
 *                    localStorage remembers the vote so the widget doesn't re-ask
 *   path progress  → localStorage learn:progress:<path>
 * Nothing here can pretend a server exists: every fetch is caught, and the UI
 * commits optimistically then stays committed, which is the phase-1 contract.
 *
 * Search on topic pages reuses window.LearnSearch from learn-search.js (the
 * generated file) — this script never re-implements scoring.
 */
(function () {
  "use strict";

  var LS = {
    get: function (k, d) { try { var v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode: state just doesn't persist */ } }
  };
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return [].slice.call((root || document).querySelectorAll(sel)); };
  var PROXY = "/apps/learn/api";   // App Proxy prefix, §4

  function post(path, body) {
    // Fire-and-forget to the proxy. Errors are swallowed on purpose: in phase 1
    // there is no backend, and a failed POST must never break the page.
    try {
      return fetch(PROXY + path, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {})
      }).catch(function () {});
    } catch (e) { return Promise.resolve(); }
  }

  /* ---------- like / save (client-only, §3c localStorage merge later) ---------- */

  function initToggles() {
    $$("[data-learn-toggle]").forEach(function (btn) {
      var kind = btn.getAttribute("data-learn-toggle");     // "like" | "save"
      var handle = btn.getAttribute("data-handle");
      if (!kind || !handle) return;
      var key = "learn:" + kind + "s";
      var set = LS.get(key, []);
      var on = set.indexOf(handle) >= 0;
      btn.setAttribute("aria-pressed", on ? "true" : "false");
      btn.addEventListener("click", function () {
        var cur = LS.get(key, []);
        var i = cur.indexOf(handle);
        var now = i < 0;
        if (now) cur.push(handle); else cur.splice(i, 1);
        LS.set(key, cur);
        btn.setAttribute("aria-pressed", now ? "true" : "false");
        var lbl = btn.querySelector("[data-label]");
        if (lbl) lbl.textContent = now ? (btn.getAttribute("data-on-label") || "Saved") : (btn.getAttribute("data-off-label") || "Save");
        post("/" + kind, { handle: handle, on: now });
      });
    });
    // Save buttons rendered by learn-card.liquid share the same store.
    $$("[data-learn-save]").forEach(function (btn) {
      var handle = btn.getAttribute("data-learn-save");
      var saves = LS.get("learn:saves", []);
      btn.setAttribute("aria-pressed", saves.indexOf(handle) >= 0 ? "true" : "false");
      btn.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        var cur = LS.get("learn:saves", []);
        var i = cur.indexOf(handle), now = i < 0;
        if (now) cur.push(handle); else cur.splice(i, 1);
        LS.set("learn:saves", cur);
        btn.setAttribute("aria-pressed", now ? "true" : "false");
        var lbl = btn.querySelector("span:last-child");
        if (lbl) lbl.textContent = now ? "Saved" : "Save";
        post("/save", { handle: handle, on: now });
      });
    });
  }

  /* ---------- share (§6 learn-share) ---------- */

  function initShare() {
    $$("[data-learn-share]").forEach(function (wrap) {
      var url = wrap.getAttribute("data-url") || location.href;
      var title = wrap.getAttribute("data-title") || document.title;
      var nativeBtn = $("[data-share-native]", wrap);
      if (nativeBtn) {
        if (navigator.share) {
          nativeBtn.addEventListener("click", function () { navigator.share({ title: title, url: url }).catch(function () {}); });
        } else {
          nativeBtn.hidden = true;
        }
      }
      var copyBtn = $("[data-share-copy]", wrap);
      if (copyBtn) {
        copyBtn.addEventListener("click", function () {
          var done = function () {
            // Icon button (learn-share snippet): flag it and let CSS show the
            // "Copied" tip; text button: swap the label. Either way 1.4s.
            if (copyBtn.querySelector("svg")) {
              copyBtn.setAttribute("data-done", "1");
              setTimeout(function () { copyBtn.removeAttribute("data-done"); }, 1400);
              return;
            }
            var was = copyBtn.textContent; copyBtn.textContent = "Copied";
            setTimeout(function () { copyBtn.textContent = was; }, 1400);
          };
          if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done); else done();
        });
      }
    });
  }

  /* ---------- table of contents (auto from h2/h3, §5 Article) ---------- */

  function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "section"; }

  function initToc() {
    var toc = $("[data-learn-toc]");
    var body = $("[data-learn-body]");
    if (!toc || !body) return;
    var heads = $$("h2, h3", body);
    if (heads.length < 2) { toc.hidden = true; return; }   // a TOC for one heading is noise
    var seen = {};
    var items = heads.map(function (h) {
      var id = h.id || slug(h.textContent);
      while (seen[id]) id = id + "-2";
      seen[id] = 1; h.id = id;
      return { id: id, text: h.textContent.trim(), level: h.tagName === "H3" ? 3 : 2 };
    });
    var list = $("[data-learn-toc-list]", toc) || toc;
    list.innerHTML = items.map(function (it) {
      return '<li class="learn-toc__item learn-toc__item--' + it.level + '"><a href="#' + it.id + '">' + it.text.replace(/[<>&]/g, function (c) { return { "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]; }) + "</a></li>";
    }).join("");
    toc.hidden = false;

    // Highlight the current section on scroll (desktop sticky TOC).
    if ("IntersectionObserver" in window) {
      var links = $$("a", list);
      var byId = {};
      links.forEach(function (a) { byId[a.getAttribute("href").slice(1)] = a; });
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            links.forEach(function (a) { a.removeAttribute("aria-current"); });
            var a = byId[en.target.id]; if (a) a.setAttribute("aria-current", "true");
          }
        });
      }, { rootMargin: "-20% 0px -70% 0px" });
      heads.forEach(function (h) { io.observe(h); });
    }
  }

  /* ---------- FAQ accordion (native <details>, just the "one open" nicety) ---------- */

  function initFaq() {
    $$("[data-learn-faq]").forEach(function (faq) {
      var all = $$("details", faq);
      all.forEach(function (d) {
        d.addEventListener("toggle", function () {
          if (d.open && faq.getAttribute("data-single") === "true") all.forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });
  }

  /* ---------- helpful? yes / no → what was missing (§5 Article, §3c helpful_votes) ---------- */

  function initHelpful() {
    $$("[data-learn-helpful]").forEach(function (w) {
      var handle = w.getAttribute("data-handle");
      var key = "learn:helpful:" + handle;
      var prior = LS.get(key, null);
      var yes = $("[data-helpful-yes]", w), no = $("[data-helpful-no]", w);
      var follow = $("[data-helpful-follow]", w), thanks = $("[data-helpful-thanks]", w);
      var form = $("form", follow || w);
      function showThanks() { if (follow) follow.hidden = true; if (thanks) thanks.hidden = false; if (yes) yes.disabled = true; if (no) no.disabled = true; }
      if (prior) { showThanks(); return; }
      if (yes) yes.addEventListener("click", function () {
        LS.set(key, { helpful: true, at: Date.now() });
        post("/helpful", { handle: handle, helpful: true });
        showThanks();
      });
      if (no) no.addEventListener("click", function () {
        LS.set(key, { helpful: false, at: Date.now() });
        post("/helpful", { handle: handle, helpful: false });
        no.setAttribute("aria-pressed", "true");
        if (follow) { follow.hidden = false; var ta = $("textarea", follow); if (ta) ta.focus(); }
        else showThanks();
      });
      if (form) form.addEventListener("submit", function (e) {
        e.preventDefault();
        var ta = $("textarea", form);
        post("/helpful", { handle: handle, helpful: false, missing_text: ta ? ta.value.trim().slice(0, 500) : "" });
        showThanks();
      });
    });
  }

  /* ---------- product strip: Add / View / Add all via /cart/add.js (§4) ---------- */

  function cartAdd(items) {
    // Line property _learn_source is what makes the order attributable to the
    // article — the same trick the Lab tools use (§4). Underscore-prefixed so
    // it never shows to the customer.
    return fetch("/cart/add.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function (r) { if (!r.ok) throw new Error("cart " + r.status); return r.json(); });
  }

  function initProductStrip() {
    $$("[data-learn-products]").forEach(function (strip) {
      var source = strip.getAttribute("data-source") || "";
      var flash = function (btn, txt) { var was = btn.textContent; btn.textContent = txt; btn.disabled = true; setTimeout(function () { btn.textContent = was; btn.disabled = false; }, 1500); };
      $$("[data-add-variant]", strip).forEach(function (btn) {
        btn.addEventListener("click", function () {
          var id = Number(btn.getAttribute("data-add-variant"));
          if (!id) return;
          cartAdd([{ id: id, quantity: 1, properties: { _learn_source: source } }])
            .then(function () { flash(btn, "Added"); document.dispatchEvent(new CustomEvent("learn:cart-added", { detail: { source: source, ids: [id] } })); })
            .catch(function () { flash(btn, "Couldn’t add"); });
        });
      });
      var all = $("[data-add-all]", strip);
      if (all) all.addEventListener("click", function () {
        var ids = $$("[data-add-variant]", strip).map(function (b) { return Number(b.getAttribute("data-add-variant")); }).filter(Boolean);
        if (!ids.length) return;
        cartAdd(ids.map(function (id) { return { id: id, quantity: 1, properties: { _learn_source: source } }; }))
          .then(function () { flash(all, "Added " + ids.length); document.dispatchEvent(new CustomEvent("learn:cart-added", { detail: { source: source, ids: ids } })); })
          .catch(function () { flash(all, "Couldn’t add"); });
      });
    });
  }

  /* ---------- video facade → embed on click (§5 Video, §13 facades not iframes) ---------- */

  var EMBED = {
    youtube:        function (id) { return "https://www.youtube-nocookie.com/embed/" + id + "?autoplay=1&rel=0"; },
    youtube_short:  function (id) { return "https://www.youtube-nocookie.com/embed/" + id + "?autoplay=1&rel=0"; },
    // TikTok's /embed/v2/ endpoint renders the entire social-post card. That
    // card is taller than a 9:16 video and gets cropped inside our player.
    // /player/v1/ is TikTok's documented player-only surface; keep the normal
    // playback controls while omitting the post description and music panel.
    tiktok:         function (id) { return "https://www.tiktok.com/player/v1/" + id + "?autoplay=1&muted=0&description=0&music_info=0&rel=0"; },
    instagram_reel: function (id) { return "https://www.instagram.com/reel/" + id + "/embed"; },
    instagram_post: function (id) { return "https://www.instagram.com/p/" + id + "/embed"; }
  };

  function initFacades() {
    $$("[data-learn-facade]").forEach(function (f) {
      var platform = f.getAttribute("data-platform"), id = f.getAttribute("data-video-id");
      var embeddable = f.getAttribute("data-embeddable") !== "false";
      var play = $("[data-facade-play]", f) || f;
      if (!embeddable || !EMBED[platform] || !id) return;   // preview-card path: plain link, nothing to wire
      play.addEventListener("click", function (e) {
        e.preventDefault();
        var vertical = platform === "youtube_short" || platform === "tiktok" || platform === "instagram_reel";
        var iframe = document.createElement("iframe");
        iframe.src = EMBED[platform](id);
        iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture");
        iframe.setAttribute("allowfullscreen", "");
        iframe.setAttribute("title", f.getAttribute("data-title") || "Video");
        iframe.setAttribute("loading", "eager");
        f.classList.add("is-playing");
        if (vertical) f.classList.add("is-vertical");
        f.innerHTML = "";
        f.appendChild(iframe);
        document.dispatchEvent(new CustomEvent("learn:video-play", { detail: { platform: platform, id: id } }));
        // Sticky mini-player once the real player scrolls away (mobile-first, §5).
        initMiniPlayer(f);
      }, { once: true });
    });
  }

  // Older video drafts stored their primary facade in article.body as well as
  // rendering it in article.learn-video. Remove only exact platform/id matches
  // from the body, preserving any genuinely additional embedded videos.
  function removePrimaryFacadeDuplicates() {
    var page = $('[data-content-type="video"]');
    if (!page) return;
    var primary = $$('[data-learn-facade]', page).filter(function (f) { return !f.closest('.learn-body'); })[0];
    if (!primary) return;
    var platform = primary.getAttribute('data-platform');
    var id = primary.getAttribute('data-video-id');
    if (!platform || !id) return;
    $$('.learn-body .learn-facade', page).forEach(function (f) {
      if (f.getAttribute('data-platform') === platform && f.getAttribute('data-video-id') === id) f.remove();
    });
  }

  function initMiniPlayer(f) {
    if (!("IntersectionObserver" in window) || window.matchMedia("(min-width: 901px)").matches) return;
    var io = new IntersectionObserver(function (entries) {
      var en = entries[0];
      f.classList.toggle("is-mini", !en.isIntersecting && en.boundingClientRect.top < 0);
    }, { threshold: 0.2 });
    io.observe(f.parentElement || f);
    var close = document.createElement("button");
    close.type = "button"; close.className = "learn-facade__mini-close"; close.setAttribute("aria-label", "Close mini player"); close.textContent = "×";
    close.addEventListener("click", function () { io.disconnect(); f.classList.remove("is-mini"); close.remove(); });
    f.appendChild(close);
  }

  /* ---------- learning path progress (localStorage now, account later §3c) ---------- */

  function initPath() {
    var path = $("[data-learn-path]");
    if (!path) return;
    var handle = path.getAttribute("data-handle");
    var key = "learn:progress:" + handle;
    var done = LS.get(key, []);
    var lessons = $$("[data-lesson]", path);
    var total = lessons.length;
    function paint() {
      var n = 0, next = null;
      lessons.forEach(function (li) {
        var h = li.getAttribute("data-lesson");
        var ok = done.indexOf(h) >= 0;
        li.classList.toggle("is-done", ok);
        var tick = $("[data-lesson-tick]", li); if (tick) tick.setAttribute("aria-checked", ok ? "true" : "false");
        if (ok) n++; else if (!next) next = li;
      });
      lessons.forEach(function (li) { li.classList.toggle("is-next", li === next); });
      var bar = $("[data-progress-bar]", path), txt = $("[data-progress-text]", path), cta = $("[data-progress-cta]", path);
      var pct = total ? Math.round((n / total) * 100) : 0;
      if (bar) { bar.style.width = pct + "%"; bar.parentElement && bar.parentElement.setAttribute("aria-valuenow", String(n)); }
      if (txt) txt.textContent = n + " of " + total + " lesson" + (total === 1 ? "" : "s") + " completed";
      if (cta) {
        if (!total) cta.hidden = true;
        else if (n === total) { cta.textContent = "Start again"; cta.href = lessons[0] ? $("a", lessons[0]).href : "#"; }
        else if (n === 0) { cta.textContent = "Start"; cta.href = $("a", lessons[0]).href; }
        else { var idx = lessons.indexOf(next) + 1; cta.textContent = "Continue lesson " + idx + " of " + total; cta.href = $("a", next).href; }
      }
    }
    // Mark-complete controls on the path page itself (a lesson page would mark
    // itself complete on the article_completed event, §11 — phase 2).
    lessons.forEach(function (li) {
      var tick = $("[data-lesson-tick]", li);
      if (!tick) return;
      tick.addEventListener("click", function (e) {
        e.preventDefault();
        var h = li.getAttribute("data-lesson"), i = done.indexOf(h);
        if (i < 0) done.push(h); else done.splice(i, 1);
        LS.set(key, done);
        post("/progress", { path: handle, lesson: h, completed: i < 0 });
        paint();
      });
    });
    paint();
  }

  /* ---------- topic filter bar → LearnSearch on that topic's items (§5 Topic) ---------- */

  function initTopicFilter() {
    var wrap = $("[data-learn-topic-filter]");
    if (!wrap || !window.LearnSearch) return;
    var grid = $("[data-topic-grid]"), topic = wrap.getAttribute("data-topic"), indexUrl = wrap.getAttribute("data-index-url");
    var empty = $("[data-topic-empty]");
    if (!grid || !indexUrl) return;
    var items = null;
    var render = function () {
      if (!items) return;
      var type = ($("[data-topic-filter='type']", wrap) || {}).value || "";
      var diff = ($("[data-topic-filter='difficulty']", wrap) || {}).value || "";
      var sort = ($("[data-topic-sort]", wrap) || {}).value || "newest";
      var out = window.LearnSearch.search(items, "", { filters: { topic: topic, type: type || undefined, difficulty: diff || undefined }, sort: sort });
      grid.innerHTML = out.map(cardHtml).join("");
      if (empty) empty.hidden = out.length > 0;
      // New cards → new save buttons.
      initToggles();
    };
    var load = function () {
      if (items) { render(); return; }
      fetch(indexUrl, { credentials: "omit" }).then(function (r) { return r.json(); }).then(function (d) {
        items = ((d && d.items) || []).map(window.LearnSearch.prepare);
        render();
      }).catch(function () { if (empty) { empty.hidden = false; empty.textContent = "Couldn’t load this topic’s content right now."; } });
    };
    $$("select", wrap).forEach(function (s) { s.addEventListener("change", load); });
    load();
  }

  // Minimal client-side twin of learn-card.liquid at density=standard, used
  // ONLY by the topic filter (server renders the initial grid; this re-renders
  // after a filter change). Same class names so the CSS is shared. If the
  // snippet changes shape, this must too — same rule as the harness mock.
  var TYPE_LABEL = { article: "Article", guide: "Guide", tutorial: "Tutorial", video: "Video", trade_update: "Trade update" };
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  function ukDate(iso) { var d = new Date(iso); return isNaN(d) ? "" : d.toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "long", year: "numeric" }); }
  function cardHtml(it, density) {
    density = density || "standard";
    var t = it.type || "article", lbl = TYPE_LABEL[t] || t;
    var compact = density === "compact";
    var meta = [];
    if (it.difficulty) meta.push(esc(it.difficulty));
    if (t === "video" && it.watch_seconds) meta.push(Math.max(1, Math.round(it.watch_seconds / 60)) + " min watch");
    else if (it.read_minutes) meta.push(it.read_minutes + " min read");
    if (it.updated_at) meta.push("Updated " + esc(ukDate(it.updated_at))); else if (it.published_at) meta.push("Published " + esc(ukDate(it.published_at)));
    var topicHtml = it.topic ? '<a class="learn-pill" href="/pages/topics/' + esc(it.topic) + '">' + esc(String(it.topic).replace(/-/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); })) + "</a>" : "";
    return '<article class="learn-card learn-card--' + density + '" data-type="' + esc(t) + '">' +
      (compact ? "" : '<div class="learn-card__media"><span class="learn-card__media--empty">' + esc(lbl) + "</span></div>") +
      '<div class="learn-card__body"><div class="learn-card__top"><span class="learn-badge" data-type="' + esc(t) + '">' + esc(lbl) + "</span>" + topicHtml + "</div>" +
      '<h3 class="learn-card__title">' + esc(it.title) + "</h3>" +
      (!compact && it.description ? '<p class="learn-card__desc">' + esc(it.description) + "</p>" : "") +
      '<p class="learn-card__meta">' + meta.map(function (m) { return "<span>" + m + "</span>"; }).join("") + "</p>" +
      (compact ? "" : '<div class="learn-card__foot"><span></span><button type="button" class="learn-save" data-learn-save="' + esc(it.handle) + '" aria-pressed="false" aria-label="Save ' + esc(it.title) + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg><span>Save</span></button></div>') +
      '</div><a class="learn-card__link" href="' + esc(it.url) + '">' + esc(it.title) + "</a></article>";
  }

  /* ---------- recent views (feeds My learning + recommendations) ---------- */

  function trackView() {
    var body = $("[data-learn-body]");
    if (!body) return;
    var handle = body.getAttribute("data-handle");
    if (!handle) return;
    var list = LS.get("learn:recent", []).filter(function (x) { return x.handle !== handle; });
    list.unshift({ handle: handle, at: Date.now() });
    LS.set("learn:recent", list.slice(0, 50));   // §3c: last 50 per user
  }

  /* ---------- follows (topics / paths / trade watch) ---------- */

  function initFollows() {
    $$("[data-learn-follow]").forEach(function (btn) {
      var kind = btn.getAttribute("data-learn-follow"), handle = btn.getAttribute("data-handle") || kind;
      var key = "learn:follows";
      var id = kind + ":" + handle;
      var on = LS.get(key, []).indexOf(id) >= 0;
      var paint = function (state) {
        btn.setAttribute("aria-pressed", state ? "true" : "false");
        var lbl = btn.querySelector("[data-label]") || btn;
        lbl.textContent = state ? (btn.getAttribute("data-on-label") || "Following") : (btn.getAttribute("data-off-label") || "Follow");
      };
      paint(on);
      btn.addEventListener("click", function () {
        var cur = LS.get(key, []), i = cur.indexOf(id), now = i < 0;
        if (now) cur.push(id); else cur.splice(i, 1);
        LS.set(key, cur); paint(now);
        post("/follow", { kind: kind, handle: handle, on: now });
      });
    });
  }

  /* ---------- My learning (§5) — everything from localStorage in phase 1 ---------- */

  function initDashboard() {
    var root = $("[data-learn-dashboard]");
    if (!root || !window.LearnSearch) return;
    var indexUrl = root.getAttribute("data-index-url");
    // No index URL: the page is Liquid-rendered (Shopify's own results as cards) and
    // learn-search-results.liquid owns the status line — leave the DOM alone.
    if (!indexUrl) return;
    var rankings = null;
    try { rankings = JSON.parse(root.getAttribute("data-rankings") || "null"); } catch (e) {}
    var items = null, byHandle = {};

    var recent = LS.get("learn:recent", []);
    var saves = LS.get("learn:saves", []);
    var likes = LS.get("learn:likes", []);
    var follows = LS.get("learn:follows", []);
    var completed = [];   // articles marked done through path progress
    Object.keys(localStorage).forEach(function (k) {
      if (k.indexOf("learn:progress:") === 0) completed = completed.concat(LS.get(k, []));
    });

    var slot = function (name) { return $("[data-dash='" + name + "']", root); };
    var fill = function (name, arts, density, emptyText) {
      var el = slot(name); if (!el) return;
      var grid = $("[data-dash-grid]", el) || el, empty = $("[data-dash-empty]", el);
      var list = arts.filter(Boolean);
      grid.innerHTML = list.map(function (it) { return cardHtml(it, density || "standard"); }).join("");
      if (empty) { empty.hidden = list.length > 0; if (emptyText) empty.textContent = emptyText; }
      var count = $("[data-dash-count]", el); if (count) count.textContent = list.length ? String(list.length) : "";
    };
    var lookup = function (handles) { return handles.map(function (h) { return byHandle[typeof h === "string" ? h : h.handle]; }); };

    function render() {
      // Continue learning: paths with progress > 0 and < total. We only know
      // path membership from the index's `path` field.
      var pathsSeen = {};
      items.forEach(function (it) { if (it.path) (pathsSeen[it.path] = pathsSeen[it.path] || []).push(it); });
      var cont = [];
      Object.keys(pathsSeen).forEach(function (p) {
        var done = LS.get("learn:progress:" + p, []);
        var lessons = pathsSeen[p].sort(function (a, b) { return (a.lesson_number || 0) - (b.lesson_number || 0); });
        if (done.length > 0 && done.length < lessons.length) {
          var next = lessons.filter(function (l) { return done.indexOf(l.handle) < 0; })[0];
          if (next) cont.push(next);
        }
      });
      // Also: recently viewed but not completed reads as "continue"
      recent.slice(0, 6).forEach(function (r) { var it = byHandle[r.handle]; if (it && completed.indexOf(it.handle) < 0 && cont.indexOf(it) < 0) cont.push(it); });
      fill("continue", cont.slice(0, 6), "row", "Nothing in progress. Start a path or open any guide.");

      // Saved, split by tab
      var savedAll = lookup(saves).filter(Boolean);
      var savedTab = root.getAttribute("data-saved-tab") || "articles";
      fill("saved", savedAll.filter(function (i) { return savedTab === "videos" ? i.type === "video" : i.type !== "video"; }), "standard", "Nothing saved yet — the Save button on any card puts it here.");
      $$("[data-saved-tab]", root).forEach(function (b) {
        b.setAttribute("aria-selected", b.getAttribute("data-saved-tab") === savedTab ? "true" : "false");
        b.onclick = function () { root.setAttribute("data-saved-tab", b.getAttribute("data-saved-tab")); render(); };
      });

      // Following
      var fEl = slot("following");
      if (fEl) {
        var list = $("[data-dash-list]", fEl), empty = $("[data-dash-empty]", fEl);
        list.innerHTML = follows.map(function (id) {
          var parts = id.split(":"), kind = parts[0], h = parts.slice(1).join(":");
          var label = kind === "trade" ? "Trade Watch" : h.replace(/-/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
          var href = kind === "topic" ? "/pages/topics/" + h : kind === "path" ? "/pages/paths/" + h : "/pages/trade-watch";
          return '<a class="learn-pill" href="' + esc(href) + '">' + esc(kind === "trade" ? "" : kind + " · ") + esc(label) + "</a>";
        }).join("");
        if (empty) empty.hidden = follows.length > 0;
      }

      fill("recent", lookup(recent).slice(0, 8), "row", "Nothing viewed yet.");
      fill("completed", lookup(completed), "row", "No lessons completed yet.");

      // Recommended: topics from recent views + saves → popular_by_topic, minus seen; fallback trending (§12)
      var topicScore = {};
      lookup(recent.slice(0, 20)).concat(lookup(saves)).filter(Boolean).forEach(function (it) { if (it.topic) topicScore[it.topic] = (topicScore[it.topic] || 0) + 1; });
      var topTopics = Object.keys(topicScore).sort(function (a, b) { return topicScore[b] - topicScore[a]; }).slice(0, 3);
      var seen = {}; recent.forEach(function (r) { seen[r.handle] = 1; }); saves.forEach(function (h) { seen[h] = 1; }); completed.forEach(function (h) { seen[h] = 1; });
      var recs = [];
      var pbt = rankings && rankings.popular_by_topic;
      topTopics.forEach(function (t) {
        var rows = (pbt && pbt[t]) || items.filter(function (i) { return i.topic === t; }).map(function (i) { return { handle: i.handle }; });
        rows.forEach(function (r) { var it = byHandle[r.handle]; if (it && !seen[it.handle] && recs.indexOf(it) < 0) recs.push(it); });
      });
      if (recs.length < 4) {
        var tr = (rankings && rankings.trending) || items.slice(0, 8).map(function (i) { return { handle: i.handle }; });
        tr.forEach(function (r) { var it = byHandle[r.handle]; if (it && !seen[it.handle] && recs.indexOf(it) < 0) recs.push(it); });
      }
      fill("recommended", recs.slice(0, 8), "compact", "Read a few things and this fills in.");
      var why = $("[data-dash-why]", root);
      if (why) why.textContent = topTopics.length ? "Because you’ve been reading about " + topTopics.map(function (t) { return t.replace(/-/g, " "); }).join(", ") : "Popular right now";

      // Trade alerts: latest trade updates in followed regions (or all if following Trade Watch)
      var tEl = slot("trade");
      if (tEl) {
        var following = follows.indexOf("trade:trade-watch") >= 0;
        var upd = items.filter(function (i) { return i.type === "trade_update" && i.trade_status !== "expired"; })
          .sort(function (a, b) { return String(b.published_at).localeCompare(String(a.published_at)); }).slice(0, 5);
        var list2 = $("[data-dash-list]", tEl), empty2 = $("[data-dash-empty]", tEl);
        list2.innerHTML = following ? upd.map(function (u) {
          return '<div class="learn-alert"><span class="learn-alert__region"><span class="learn-region">' + esc((u.trade_regions || ["ROW"]).join("/")) + '</span></span><div><p class="learn-alert__title"><a href="' + esc(u.url) + '">' + esc(u.title) + '</a></p><span class="learn-alert__meta">' + esc(ukDate(u.published_at)) + (u.trade_status ? " · " + esc(u.trade_status.replace("_", " ")) : "") + "</span></div></div>";
        }).join("") : "";
        if (empty2) empty2.hidden = following && upd.length > 0;
      }
    }

    fetch(indexUrl, { credentials: "omit" }).then(function (r) { return r.json(); }).then(function (d) {
      items = ((d && d.items) || []).map(window.LearnSearch.prepare);
      items.forEach(function (i) { byHandle[i.handle] = i; });
      render();
    }).catch(function () {
      $$("[data-dash-empty]", root).forEach(function (e) { e.hidden = false; e.textContent = "Couldn’t load your learning right now."; });
    });
  }

  /* ---------- search results page (§5 Search) ---------- */

  function highlight(text, tokens) {
    var out = esc(text);
    tokens.forEach(function (t) {
      if (t.length < 2) return;
      out = out.replace(new RegExp("(" + t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + ")", "ig"), "<mark>$1</mark>");
    });
    return out;
  }

  function initSearchPage() {
    var root = $("[data-learn-search-page]");
    if (!root || !window.LearnSearch) return;
    var indexUrl = root.getAttribute("data-index-url");
    // No index URL: the page is Liquid-rendered (Shopify's own results as cards) and
    // learn-search-results.liquid owns the status line — leave the DOM alone.
    if (!indexUrl) return;
    var input = $("[data-sp-q]", root), results = $("[data-sp-results]", root), status = $("[data-sp-status]", root);
    var noRes = $("[data-sp-noresults]", root), prodCol = $("[data-sp-products]", root);
    var filters = $("[data-sp-filters]", root);
    var items = null, lastLogged = "";
    var popular = [];
    try { popular = JSON.parse(root.getAttribute("data-popular") || "[]"); } catch (e) {}

    var q0 = new URLSearchParams(location.search).get("q") || "";
    if (input && !input.value) input.value = q0;

    function readFilters() {
      var f = {};
      var v = function (n) { var el = $("[name='" + n + "']:checked", filters) || $("select[name='" + n + "']", filters); return el ? el.value : ""; };
      var type = v("type"), only = v("only");
      if (only === "videos") type = "video"; else if (only === "articles" && !type) f._noVideo = true;
      if (type) f.type = type;
      var topic = v("topic"); if (topic) f.topic = topic;
      var diff = v("difficulty"); if (diff) f.difficulty = diff;
      var date = v("date");
      if (date) { var d = new Date(); d.setDate(d.getDate() - Number(date)); f.since = d.toISOString().slice(0, 10); }
      return { filters: f, sort: v("sort") || undefined };
    }

    function run() {
      if (!items) return;
      var q = (input ? input.value : q0).trim();
      var o = readFilters();
      var out = window.LearnSearch.search(items, q, { filters: o.filters, sort: o.sort });
      if (o.filters._noVideo) out = out.filter(function (i) { return i.type !== "video"; });
      var tokens = window.LearnSearch.tokenise(q);
      results.innerHTML = out.map(function (it) {
        var h = cardHtml(it, "row");
        // highlight title + description inside the row card
        if (tokens.length) h = h.replace(/(<h3 class="learn-card__title">)([\s\S]*?)(<\/h3>)/, function (_, a, b, c) { return a + highlight(b.replace(/&[a-z#0-9]+;/g, function (m) { return m; }), tokens).replace(/&amp;lt;/g, "&lt;") + c; })
          .replace(/(<p class="learn-card__desc">)([\s\S]*?)(<\/p>)/, function (_, a, b, c) { return a + highlight(b, tokens) + c; });
        return h;
      }).join("");
      status.textContent = out.length + (out.length === 1 ? " result" : " results") + (q ? ' for “' + q + '”' : "");
      if (noRes) {
        noRes.hidden = out.length > 0 || !q;
        if (!out.length && q) {
          var sug = $("[data-sp-topics]", noRes);
          if (sug) {
            var seenT = {}; var ts = [];
            items.forEach(function (i) { if (i.topic && !seenT[i.topic]) { seenT[i.topic] = 1; ts.push(i.topic); } });
            sug.innerHTML = ts.slice(0, 6).map(function (t) { return '<a class="learn-pill" href="/pages/topics/' + esc(t) + '">' + esc(t.replace(/-/g, " ")) + "</a>"; }).join("");
          }
          var pop = $("[data-sp-popular]", noRes);
          if (pop) pop.innerHTML = popular.slice(0, 6).map(function (p) { return '<a class="learn-pill" href="?type=article&q=' + encodeURIComponent(p) + '">' + esc(p) + "</a>"; }).join("");
        }
      }
      // log once per distinct query (§9 search_log)
      if (q && q !== lastLogged) { lastLogged = q; post("/search-log", { q: q, results_count: out.length }); }
      // keep the URL honest for sharing/back
      if (history.replaceState) { var u = new URL(location.href); if (q) u.searchParams.set("q", q); else u.searchParams.delete("q"); u.searchParams.set("type", "article"); history.replaceState(null, "", u); }
      loadProducts(q);
      initToggles();
    }

    // Products via the native Predictive Search API — second column, never blocks results.
    var lastProdQ = null;
    function loadProducts(q) {
      if (!prodCol) return;
      if (!q || q.length < 2) { prodCol.hidden = true; return; }
      if (q === lastProdQ) return; lastProdQ = q;
      fetch("/search/suggest.json?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var ps = (d && d.resources && d.resources.results && d.resources.results.products) || [];
          var list = $("[data-sp-products-list]", prodCol);
          if (!list) return;
          list.innerHTML = ps.map(function (p) {
            return '<a class="learn-pcard" href="' + esc(p.url) + '"><span class="learn-pcard__img">' + (p.featured_image && p.featured_image.url ? '<img src="' + esc(p.featured_image.url) + '" alt="">' : "") + '</span><span><span class="learn-pcard__t">' + esc(p.title) + '</span><br><span class="learn-pcard__p">' + esc(p.price ? "£" + p.price : "") + "</span></span></a>";
          }).join("");
          prodCol.hidden = ps.length === 0;
        }).catch(function () { prodCol.hidden = true; });
    }

    // Log clicks (§9 clicked_content_id) — delegate on the results list
    results.addEventListener("click", function (e) {
      var a = e.target.closest(".learn-card__link");
      if (a) post("/search-log", { q: (input ? input.value : q0).trim(), clicked_url: a.getAttribute("href") });
    });

    if (input) { input.addEventListener("input", run); }
    if (filters) filters.addEventListener("change", run);
    var form = input && input.closest("form"); if (form) form.addEventListener("submit", function (e) { e.preventDefault(); run(); });

    fetch(indexUrl, { credentials: "omit" }).then(function (r) { return r.json(); }).then(function (d) {
      items = ((d && d.items) || []).map(window.LearnSearch.prepare);
      // populate topic filter from the index
      var tsel = $("select[name='topic']", filters);
      if (tsel && tsel.options.length <= 1) {
        var seenT = {};
        items.forEach(function (i) { if (i.topic && !seenT[i.topic]) { seenT[i.topic] = 1; var o = document.createElement("option"); o.value = i.topic; o.textContent = i.topic.replace(/-/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); }); tsel.appendChild(o); } });
      }
      run();
    }).catch(function () { status.textContent = "Search is unavailable right now."; });
  }

  /* ---------- trade board (§5 Trade Watch landing) ---------- */

  function initTradeBoard() {
    var root = $("[data-learn-trade-board]");
    if (!root) return;
    var tabs = $$("[data-region-tab]", root);
    var items = $$("[data-regions]", root);
    var region = LS.get("learn:trade-region", "all");
    function paint() {
      tabs.forEach(function (t) { t.setAttribute("aria-selected", t.getAttribute("data-region-tab") === region ? "true" : "false"); });
      items.forEach(function (it) {
        var rs = (it.getAttribute("data-regions") || "").split(",").map(function (s) { return s.trim(); }).filter(Boolean);
        var show = region === "all" || rs.indexOf(region) >= 0 || (rs.length === 0 && region === "ROW");
        it.hidden = !show;
      });
      // per-column empty notes
      $$("[data-status]", root).forEach(function (col) {
        var any = $$("[data-regions]", col).some(function (x) { return !x.hidden; });
        var e = $("[data-col-empty]", col); if (e) e.hidden = any;
      });
    }
    tabs.forEach(function (t) { t.addEventListener("click", function () { region = t.getAttribute("data-region-tab"); LS.set("learn:trade-region", region); paint(); }); });
    paint();
  }

  /* ---------- article_completed (75% scroll + dwell, §11) — event only in phase 1 ---------- */

  function initCompletion() {
    var body = $("[data-learn-body]");
    if (!body) return;
    var start = Date.now(), fired = false;
    function check() {
      if (fired) return;
      var r = body.getBoundingClientRect();
      var seen = Math.min(1, Math.max(0, (window.innerHeight - r.top) / Math.max(1, r.height)));
      if (seen >= 0.75 && Date.now() - start > 20000) {
        fired = true;
        document.dispatchEvent(new CustomEvent("learn:article-completed", { detail: { handle: body.getAttribute("data-handle") } }));
        window.removeEventListener("scroll", check);
      }
    }
    window.addEventListener("scroll", check, { passive: true });
  }

  // List pages (blog.learn / page.learn-watch): chip filters over the rendered grid.
  // data-filter="type:video" | "topic:<handle>" | "platform:tiktok" | "" (all).
  function initListFilter() {
    $$("[data-learn-list]").forEach(function (root) {
      var bar = $("[data-learn-list-filters]", root), grid = $("[data-learn-list-grid]", root);
      if (!bar || !grid) return;
      var status = $("[data-learn-list-status]", root), empty = $("[data-learn-list-empty]", root);
      var chips = $$("[data-filter]", bar), cards = $$(".learn-card", grid);
      function apply(filter) {
        var parts = (filter || "").split(":"), key = parts[0], val = parts[1], shown = 0;
        cards.forEach(function (c) {
          var ok = !key || (c.getAttribute("data-" + key) || "") === val;
          c.hidden = !ok; if (ok) shown++;
        });
        chips.forEach(function (ch) { var on = ch.getAttribute("data-filter") === (filter || ""); ch.classList.toggle("is-on", on); ch.setAttribute("aria-pressed", on ? "true" : "false"); });
        if (status) status.textContent = key ? shown + " of " + cards.length + " on this page" : "";
        if (empty) empty.hidden = shown > 0;
      }
      chips.forEach(function (ch) { ch.addEventListener("click", function () { apply(ch.getAttribute("data-filter")); }); });
      var initial = (location.hash.match(/^#filter=(.+)$/) || [])[1];
      if (initial) apply(decodeURIComponent(initial));
    });
  }

  function boot() {
    removePrimaryFacadeDuplicates();
    trackView();
    initToggles(); initShare(); initToc(); initFaq(); initHelpful();
    initProductStrip(); initFacades(); initPath(); initTopicFilter(); initCompletion();
    initFollows(); initDashboard(); initSearchPage(); initTradeBoard(); initListFilter();
    if ($(".learn-bottomnav")) document.body.classList.add("learn-has-bottomnav");
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();

  window.Learn = { cartAdd: cartAdd, post: post };
})();
